import { Dtos } from "@inrev/common";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useRequest } from "../../../utils/request";
import { boundedPoll } from "../../bounded-poll";
import {
	sfaaClassExperienceImportsQueryKey,
	sfaaClassExperienceQueryKey,
} from "../sfaa-class-experience.keys";

type Import = Dtos.Admin.SfaaClassExperience.Import.Response;

const POLL_INTERVAL_MS = 2_000;
/** Bounds polling well beyond a normal import while stale detection handles dead imports sooner. */
const POLL_MAX_MS = 10 * 60 * 1_000;

/** A stale record has stopped heartbeating and is terminal even if its status remains in flight. */
export const findInFlightSfaaImport = (imports: Import[] | undefined): Import | undefined =>
	imports?.find(
		(importRecord) =>
			Dtos.Admin.SfaaClassExperience.isImportInFlight(importRecord.status) && !importRecord.isStale,
	);

export const useFetchSfaaClassExperienceImports = () => {
	const { get } = useRequest();
	const queryClient = useQueryClient();
	const pollStartedAtMsRef = useRef<number | null>(null);

	const query = useQuery({
		queryKey: sfaaClassExperienceImportsQueryKey(),
		queryFn: async () => await get<Import[]>("/v2/admin/surety/sfaa-class-experience/imports"),
		refetchInterval: (q) =>
			boundedPoll({
				data: q.state.data,
				shouldPoll: (imports) => findInFlightSfaaImport(imports) !== undefined,
				startedAtMsRef: pollStartedAtMsRef,
				intervalMs: POLL_INTERVAL_MS,
				maxMs: POLL_MAX_MS,
			}),
	});

	// Invalidate only after observing in-flight work; otherwise every idle mount would refetch.
	const inFlight = findInFlightSfaaImport(query.data) !== undefined;
	const sawInFlightRef = useRef(false);
	useEffect(() => {
		if (inFlight) {
			sawInFlightRef.current = true;
			return;
		}
		if (!sawInFlightRef.current) return;
		sawInFlightRef.current = false;
		void queryClient.invalidateQueries({ queryKey: sfaaClassExperienceQueryKey() });
	}, [inFlight, queryClient]);

	return query;
};
